/**
 * Per-trajectory persistence for the review "ask about this run" thread.
 *
 * sessionStorage, not localStorage, and the choice is deliberate: these
 * answers quote production trace content verbatim, and `AuthContext.logout`
 * only sweeps `oidc.*` keys — anything we put in localStorage would outlive
 * the session on a shared machine. sessionStorage is per-tab and dies with
 * it, while still surviving in-app navigation and a full page refresh, which
 * is what the reviewer actually needs.
 *
 * Threads are keyed by the trace's R2 key, so switching queue items switches
 * threads and coming back restores the right one.
 */

export interface StoredMessage {
  role: 'user' | 'assistant'
  content: string
}

/**
 * Mirrors `MAX_MESSAGES` in `app/schemas/review_chat.py`.
 *
 * This bounds what we PERSIST, not what we send: the client replays its whole
 * history on every turn, so a thread sitting at the cap posts cap+1 and the
 * server rejects it. That is deliberate — a triage thread this long has
 * outlived the question it started from, and silently dropping the oldest
 * turns would answer later questions from a context the reviewer can no
 * longer see. The thread is a dead end at this point and clearing it is the
 * way forward.
 */
export const MAX_STORED_MESSAGES = 40

const STORAGE_PREFIX = 'review-chat:'

function storageKey(traceKey: string): string {
  return `${STORAGE_PREFIX}${traceKey}`
}

function isStoredMessage(value: unknown): value is StoredMessage {
  if (typeof value !== 'object' || value === null) return false
  const entry = value as Record<string, unknown>
  return (
    (entry.role === 'user' || entry.role === 'assistant') &&
    typeof entry.content === 'string'
  )
}

/** Validate a stored payload. Anything malformed reads as "no thread". */
export function parseThread(raw: string | null): StoredMessage[] {
  if (raw === null) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      console.error('review chat: stored thread is not an array:', raw)
      return []
    }
    if (!parsed.every(isStoredMessage)) {
      console.error('review chat: stored thread has malformed messages:', raw)
      return []
    }
    return trimThread(parsed)
  } catch (error) {
    console.error('review chat: unparseable stored thread:', error)
    return []
  }
}

/**
 * The newest `MAX_STORED_MESSAGES` turns, bounding what sessionStorage holds.
 * Never applied to an outgoing payload — see `MAX_STORED_MESSAGES`.
 */
export function trimThread(messages: StoredMessage[]): StoredMessage[] {
  return messages.length <= MAX_STORED_MESSAGES
    ? messages
    : messages.slice(-MAX_STORED_MESSAGES)
}

/**
 * A thread whose only turn is the automatic opener, unanswered, reads as
 * empty. An opener that failed or was aborted persists as a lone user
 * message, and it would otherwise count as "already asked" on the next
 * mount and switch off run-on-open for that item for good. Only the
 * opener is forgotten: it is a constant the next mount re-asks, while a
 * lone question the reviewer typed is their own text and is kept, as are
 * unanswered follow-ups under an answer.
 */
export function withoutUnansweredOpener(
  messages: StoredMessage[],
  opener: string,
): StoredMessage[] {
  const only = messages[0]
  if (
    messages.length === 1 &&
    only !== undefined &&
    only.role === 'user' &&
    only.content === opener
  ) {
    return []
  }
  return messages
}

/** `opener` is the question the panel sends on its own; see `withoutUnansweredOpener`. */
export function loadThread(traceKey: string, opener: string): StoredMessage[] {
  return withoutUnansweredOpener(
    parseThread(sessionStorage.getItem(storageKey(traceKey))),
    opener,
  )
}

export function saveThread(traceKey: string, messages: StoredMessage[]): void {
  const key = storageKey(traceKey)
  if (messages.length === 0) {
    sessionStorage.removeItem(key)
    return
  }
  try {
    sessionStorage.setItem(key, JSON.stringify(trimThread(messages)))
  } catch (error) {
    // Quota is a throw, not a silent failure, and losing a saved thread is
    // never worth breaking the panel the reviewer is using.
    console.error('review chat: could not persist thread:', error)
  }
}

export function clearThread(traceKey: string): void {
  sessionStorage.removeItem(storageKey(traceKey))
}
